import { Field } from "@/types/Field";
import { DataType } from "@/types/enums/DataType";
import {
  GuestDetailFieldWidth,
  GuestDetailGroupWidth,
  GuestDetailLayout,
  GuestDetailLayoutGroup,
  GuestDetailSectionConfig,
} from "@/types/Event";
import { getFieldKey, findFieldByKey } from "../../ConditionBuilder/condition-utils";

export type {
  GuestDetailFieldWidth,
  GuestDetailGroupWidth,
  GuestDetailLayout,
  GuestDetailLayoutGroup,
  GuestDetailSectionConfig,
};

/**
 * Fields-string of the layout subscription — the ONE cache key every layout
 * consumer (popup view, sidebar tabs, action bar, picker, builder) reads and
 * the builder's post-save cache write targets. `updatedAt` rides along as the
 * builder's If-Match CAS token for the named-layouts array PUT.
 */
export const LAYOUT_FIELDS = "guestDetailLayout,guestDetailLayouts,updatedAt";

/** Max named layouts per event — mirror of EventService.GDL_MAX_NAMED_LAYOUTS. */
export const MAX_NAMED_LAYOUTS = 16;

/** Right-column tab ids, in default display order (GuestNotes.tsx). Must stay
 *  in sync with the backend vocabulary (EventService.GDL_SIDEBAR_TAB_KEYS) —
 *  with ONE deliberate exception: "requests" was merged into "appointments"
 *  and removed here, but the BE keeps accepting the key (accept-but-ignore)
 *  so stored layouts from before the merge still validate on save. Unknown
 *  keys are dropped on read (resolveSectionConfig), so those layouts degrade
 *  gracefully. */
export const SIDEBAR_TAB_KEYS = [
  "appointments",
  "sentMails",
  "companions",
  "doku",
  "seating",
  "attachments",
] as const;
export type SidebarTabKey = (typeof SIDEBAR_TAB_KEYS)[number];

/** Header action ids, in default display order (guest-actions/Actions.tsx).
 *  Must stay in sync with EventService.GDL_ACTION_KEYS. */
export const ACTION_KEYS = [
  "checkIn",
  "sendEmail",
  "sendSms",
  "website",
  "qrCode",
  "portalLink",
  "archive",
  "delete",
] as const;
export type ActionKey = (typeof ACTION_KEYS)[number];

/**
 * Apply an {order, hidden} section config to a fixed key vocabulary:
 * unknown keys are dropped, keys missing from `order` come back (so a key
 * added later never silently vanishes), `hidden` becomes a Set. Callers apply
 * their plugin/capability gates AFTER this — the config can only
 * reorder/reduce.
 *
 * Where a missing key lands is the caller's choice:
 *  - `"append"` (default): after everything the config placed, in default
 *    order. The configured neighbours stay neighbours — right for rails and
 *    tab strips, whose tests pin exactly that.
 *  - `"slot"`: right after the nearest default predecessor the config DID
 *    place (no placed predecessor ⇒ append). For the guest popup's header
 *    actions, where the strip ends in the red archive/delete block: a
 *    copy-portal-link action added in 2026-09 must sit next to the QR code
 *    for every event that saved a layout before it existed, not behind Delete.
 */
export function resolveSectionConfig(
  config: GuestDetailSectionConfig | undefined,
  allKeys: readonly string[],
  opts?: { missing?: "append" | "slot" },
): { order: string[]; hidden: Set<string> } {
  const known = new Set(allKeys);
  const order = (config?.order ?? []).filter((k) => known.has(k));
  const placed = new Set(order);
  const slot = opts?.missing === "slot";
  allKeys.forEach((key, idx) => {
    if (placed.has(key)) return;
    let at = order.length;
    if (slot) {
      for (let i = idx - 1; i >= 0; i--) {
        const anchor = order.indexOf(allKeys[i]!);
        if (anchor !== -1) {
          at = anchor + 1;
          break;
        }
      }
    }
    order.splice(at, 0, key);
    placed.add(key);
  });
  const hidden = new Set((config?.hidden ?? []).filter((k) => known.has(k)));
  return { order, hidden };
}

/**
 * Built-in guest fields the detail popup can render as an EDITABLE input.
 * Order = default-layout order. Display-only built-ins live in
 * {@link READONLY_BUILTIN_ORDER} and are deliberately kept out of this list —
 * see the opt-in contract documented there.
 */
export const PLACEABLE_BUILTIN_ORDER = [
  "guestType",
  "state",
  "gender",
  "title",
  "language",
  "firstName",
  "lastName",
  "company",
  "jobTitle",
  "username",
  "code",
  "address",
  "address2",
  "zipCode",
  "city",
  "country",
  "email",
  "emailOptIn",
  "phone",
  "mobile",
  // SMS channel wave: the SMS consent next to the numbers it governs. Only
  // placed when the field exists — FieldsProvider drops it while the SMS
  // channel is off, and a layout never conjures a field the catalogue lacks.
  "smsOptIn",
] as const;

/**
 * Built-ins the popup renders as DISPLAY-ONLY text: no input, never saved.
 *
 * OPT-IN by construction. They are NOT in {@link PLACEABLE_BUILTIN_ORDER}, so
 * `buildDefaultLayout` never emits them and `resolveLayout` never auto-appends
 * them into a layout an admin already saved. They surface on the builder's
 * NOT-PLACED shelf and exist in a layout only because someone put them there.
 *
 * Membership is this list, NOT `field.editable`. `editable:false` is necessary
 * but nowhere near sufficient — 34 FIELDS_GUEST entries qualify, and
 * `companions` recursively mounts PGuestDetails, `checkInOutCTA` renders a live
 * check-in CTA, and the 18 activity rollups would print raw id arrays. In the
 * other direction `archived` is `editable:true` and must stay unplaceable. So
 * the flag is never consulted here.
 *
 * ⚠ `checkedIn`/`checkedInAt` are only honest because the popup RE-FETCHES the
 * guest after a check-in. Two things carry that, and both are load-bearing:
 *   1. `PGuestDetails` listens for the `checkInActionCompleted` window event
 *      (dispatched by the popup's own header button, the table CTA, the picker
 *      and the bulk job) and re-pulls one row — the header button keeps its
 *      result in local state only, and the backend additionally stamps
 *      `checkedInAt`, which an optimistic toggle cannot know.
 *   2. That listener is registered behind `resolvedEventId`. The guest-list row
 *      carries NO `eventId` (`GuestDTO` has no such field, so asking for it in
 *      the projection is silently dropped) — the id comes from the surrounding
 *      GuestsProvider / EventContext instead. Read the id off the row again and
 *      every refetch path goes dead for exactly the popups opened from the list.
 * Break either and these two cells contradict the button inches above them —
 * drop them from this list rather than shipping the lie.
 */
export const READONLY_BUILTIN_ORDER = [
  "origin",
  "importId",
  "createdAt",
  "checkedIn",
  "checkedInAt",
  "numCompanions",
] as const;

const READONLY_BUILTIN_SET = new Set<string>(READONLY_BUILTIN_ORDER);

/**
 * The guest popup's "Portal-Link" cell (W3-3, PB-FB-L22): a button that mints
 * this guest's portal link and copies it — the header action's twin, placed
 * where the admin wants it in the field grid.
 *
 * A LAYOUT-ONLY field. There is no guest data behind it, so it is in none of
 * the catalogues (`FIELDS_GUEST` & co.), FieldsProvider never delivers it and a
 * role's field allowlist never governs it (`ALLOWLIST_EXEMPT_FIELDS`) — the
 * capabilities decide, exactly like for the header action. Its own key on
 * purpose: `portalLink` is already the header ACTION id and the export column,
 * and either collision would make one switch move the other.
 *
 * `withLayoutOnlyFields` puts it into the field lookup of `placeableFields`,
 * `fieldByKey` and therefore `resolveLayout`; without that the resolver prunes
 * a placed cell as "field no longer exists" and the builder's next save makes
 * the removal permanent. Opt-in like the display-only built-ins
 * ({@link OPT_IN_LAYOUT_KEYS}), detail popup only (never a create form).
 */
export const PORTAL_LINK_LAYOUT_KEY = "guestPortalLink";

const PORTAL_LINK_LAYOUT_FIELD: Field = {
  name: PORTAL_LINK_LAYOUT_KEY,
  // Never consulted for rendering — the cell has its own branch in
  // GuestLayoutFieldInput. ACTION is the catalogue's word for "a button, not a
  // value" (the check-in CTA column carries it too).
  dataType: DataType.ACTION,
  additionalField: false,
  editable: false,
};

/**
 * The guest popup's "Ankunft melden an" cell — plan arrival-automation Rev. 4
 * §1.8: which notification contacts hear about THIS guest's arrival (chips),
 * and a "Bearbeiten" that opens a dialog writing at once (outside the popup's
 * save / discard). Replaces the W4 Hop B block under the layout.
 *
 * A LAYOUT-ONLY field like {@link PORTAL_LINK_LAYOUT_KEY} (no guest data
 * behind it, in no catalogue, exempt from a role's field allowlist —
 * `ALLOWLIST_EXEMPT_FIELDS`; OWNER / ADMIN decide, the backend's `authorize()`
 * answers everyone else 403), detail popup only (never a create form). But a
 * DEFAULT key, NOT opt-in: it is in every default layout and the resolver
 * auto-appends it to a layout saved before it existed — an opt-in cell would
 * hide the links every guest already has (plan Rev. 2 C-M9). An admin may
 * still move it, or hide it like any field.
 */
export const ARRIVAL_NOTICE_LAYOUT_KEY = "guestArrivalNotice";

const ARRIVAL_NOTICE_LAYOUT_FIELD: Field = {
  name: ARRIVAL_NOTICE_LAYOUT_KEY,
  // Never consulted for rendering — the cell has its own branch in
  // GuestLayoutFieldInput (see PORTAL_LINK_LAYOUT_FIELD).
  dataType: DataType.ACTION,
  additionalField: false,
  editable: false,
};

/**
 * Keys that are in a layout ONLY because an admin put them there: the
 * display-only built-ins and the portal-link cell. Every opt-in site uses this
 * set — the default layout, the resolver's auto-append and `hidden`
 * normalization, the builder's hide / delete-group / not-placed shelf and the
 * "Remove from view" wording.
 *
 * Deliberately NOT {@link READONLY_BUILTIN_ORDER} itself: that list also means
 * "renders as inert text" (`isReadOnlyLayoutField`), and the portal cell is a
 * live button.
 */
export const OPT_IN_LAYOUT_KEYS: readonly string[] = [
  ...READONLY_BUILTIN_ORDER,
  PORTAL_LINK_LAYOUT_KEY,
];

const OPT_IN_LAYOUT_SET = new Set<string>(OPT_IN_LAYOUT_KEYS);

/**
 * Placement vocabulary of the DETAIL popup: editable built-ins first, then the
 * display-only tail, then the layout-only portal cell. This is
 * `placeableFields`/tray order — NOT a default-layout order, since
 * `buildDefaultLayout` filters the opt-in tail back out.
 */
const ALL_PLACEABLE_BUILTIN_ORDER: readonly string[] = [
  ...PLACEABLE_BUILTIN_ORDER,
  ...READONLY_BUILTIN_ORDER,
  PORTAL_LINK_LAYOUT_KEY,
  ARRIVAL_NOTICE_LAYOUT_KEY,
];

const PLACEABLE_BUILTIN_SET = new Set<string>(ALL_PLACEABLE_BUILTIN_ORDER);

/**
 * Which surface family a layout is being resolved FOR.
 *
 * `"detail"` is the guest-detail popup (the original, unchanged behavior and
 * the default of every function below, so existing call sites keep working).
 * `"create"` is an admin CREATE form — a smaller vocabulary, see
 * {@link CREATE_PLACEABLE_BUILTIN_ORDER}.
 */
export type LayoutVariant = "detail" | "create";

/**
 * Built-ins a CREATE form can place. Two groups are deliberately absent:
 *
 *  - `username` / `code` — the credential cells. Their widget is an inline
 *    SAVE flow against an existing guest (`onSaveCredential`), and the values
 *    are minted server-side on insert; the pre-layout create dialog therefore
 *    rendered them only when `create === false` (GeneralInfos). Placing one on
 *    a create form would show an edit affordance that cannot persist anything.
 *  - every {@link READONLY_BUILTIN_ORDER} key (`createdAt`, `checkedIn`,
 *    `origin`, …) — they describe a guest that does not exist yet, so the cell
 *    could only ever render an em-dash. The same holds for the layout-only
 *    {@link PORTAL_LINK_LAYOUT_KEY} cell: no guest, no link to mint.
 *  - `smsOptIn` (SMS channel wave) — the create payload carries no SMS consent
 *    (`GuestCreationFields` has no such key), so a placed cell would accept a
 *    choice the create call silently drops. SMS consent is recorded on the
 *    existing guest (detail popup, grid cell, import).
 *
 * Order is inherited from {@link PLACEABLE_BUILTIN_ORDER} (the filter keeps
 * it), so the default create layout keeps the field order of the dialog it
 * replaces.
 */
export const CREATE_PLACEABLE_BUILTIN_ORDER: readonly string[] =
  PLACEABLE_BUILTIN_ORDER.filter((n) => n !== "username" && n !== "code" && n !== "smsOptIn");

const CREATE_PLACEABLE_BUILTIN_SET = new Set<string>(
  CREATE_PLACEABLE_BUILTIN_ORDER,
);

/** Placement vocabulary of one variant, in tray order. */
function builtinOrderOf(variant: LayoutVariant): readonly string[] {
  return variant === "create"
    ? CREATE_PLACEABLE_BUILTIN_ORDER
    : ALL_PLACEABLE_BUILTIN_ORDER;
}

function builtinSetOf(variant: LayoutVariant): Set<string> {
  return variant === "create"
    ? CREATE_PLACEABLE_BUILTIN_SET
    : PLACEABLE_BUILTIN_SET;
}

/** Read-only test on a LAYOUT KEY. Built-in keys are bare names, so a custom
 *  field (`additionalFields.<name>`) can never match. For "is this key opt-in?"
 *  (what the resolver and the builder ask) use {@link isOptInLayoutKey}. */
export function isReadOnlyLayoutKey(key: string): boolean {
  return READONLY_BUILTIN_SET.has(key);
}

/** Read-only test on a FIELD — the renderer's "inert text" branch, and only
 *  that. The `!additionalField` guard is load-bearing: a CUSTOM field literally
 *  named `createdAt` keeps its own editable input. */
export function isReadOnlyLayoutField(field: Field): boolean {
  return !field.additionalField && READONLY_BUILTIN_SET.has(field.name);
}

/** Opt-in test on a LAYOUT KEY — see {@link OPT_IN_LAYOUT_KEYS}. */
export function isOptInLayoutKey(key: string): boolean {
  return OPT_IN_LAYOUT_SET.has(key);
}

/** Opt-in test on a FIELD; same custom-field guard as `isReadOnlyLayoutField`. */
export function isOptInLayoutField(field: Field): boolean {
  return !field.additionalField && OPT_IN_LAYOUT_SET.has(field.name);
}

/** The portal-link cell's FIELD test — the renderer's own branch. */
export function isPortalLinkLayoutField(field: Field): boolean {
  return !field.additionalField && field.name === PORTAL_LINK_LAYOUT_KEY;
}

/** The "Ankunft melden an" cell's FIELD test — the renderer's own branch. */
export function isArrivalNoticeLayoutField(field: Field): boolean {
  return !field.additionalField && field.name === ARRIVAL_NOTICE_LAYOUT_KEY;
}

/** One result per `allFields` array: the lookup runs per cell per render, and
 *  FieldsProvider hands out a stable array. */
const withLayoutOnlyCache = new WeakMap<Field[], Field[]>();

/**
 * THE field pool every placement question goes through — `placeableFields`,
 * `fieldByKey` and through them `resolveLayout`/`buildDefaultLayout`: the
 * caller's fields plus the layout-only portal cell, on the detail popup.
 * A create form gets the caller's fields unchanged (the cell describes a guest
 * that does not exist yet). Deliberately here and not in
 * `condition-utils.findFieldByKey`, which the condition builder and the export
 * share — a portal button must never become a filter or an export column.
 */
function withLayoutOnlyFields(allFields: Field[], variant: LayoutVariant): Field[] {
  if (variant === "create") return allFields;
  const cached = withLayoutOnlyCache.get(allFields);
  if (cached) return cached;
  // The portal-link cell and (plan arrival-automation Rev. 4 §1.8) the
  // "Ankunft melden an" cell — each added only when the caller's fields do
  // not already carry a built-in of that name.
  const missing = [PORTAL_LINK_LAYOUT_FIELD, ARRIVAL_NOTICE_LAYOUT_FIELD].filter(
    (lf) => !allFields.some((f) => !f.additionalField && f.name === lf.name),
  );
  const pool = missing.length === 0 ? allFields : [...allFields, ...missing];
  withLayoutOnlyCache.set(allFields, pool);
  return pool;
}

const CONTACT_BUILTINS = new Set([
  "address",
  "address2",
  "zipCode",
  "city",
  "country",
  "email",
  "emailOptIn",
  "phone",
  "mobile",
  "smsOptIn",
]);

/**
 * CONTAINER-query grid system, shared by the real popup and the builder
 * canvas so the preview is pixel-true. Viewport breakpoints are wrong here:
 * the actual space is "popup minus notes column" / "canvas minus drawer",
 * so every grid keys off its own container width instead.
 *
 * - The panel hosting the groups declares `@container`; the group grid goes
 *   2-column at ≥42rem panel width (`@2xl`), and each group spans per its
 *   width.
 * - Each GROUP declares `@container` too; its field grid is the full 6-col
 *   design grid from 20rem (`@xs`), 1-col below (true-mobile widths only).
 *   The threshold must stay LOW: the popup and the canvas are both capped at
 *   72rem, so a HALF group tops out around 30rem — a mid breakpoint (@md+)
 *   would flatten FULL/HALF/THIRD into indistinguishable columns at every
 *   real size (that was a live bug: the design grid formerly sat behind
 *   @2xl = 42rem, which a HALF group can mathematically never reach).
 */
export const CQ_GROUP_GRID =
  "grid grid-cols-1 @2xl:grid-cols-2 gap-x-8 gap-y-6 items-start w-full";

export const CQ_GROUP_WIDTH_CLASS: Record<GuestDetailGroupWidth, string> = {
  FULL: "@2xl:col-span-2",
  HALF: "@2xl:col-span-1",
};

/**
 * The 6-column design grid engages at @xs (20rem CONTAINER width).
 *
 * ⚠ A phone puts ~326px into this container and a HALF group inside a
 * full-width DESKTOP popup measures ~324px — the same number. A container
 * query cannot tell those two apart, so there is NO threshold that makes the
 * phone denser without also changing the desktop. Moving this to @sm was tried
 * and measured: it dropped desktop HALF groups from 6 tracks to 2. If the phone
 * density is to be improved, it needs a mechanism that knows about the
 * VIEWPORT, not the container.
 *
 * The upper bound still holds too: popup and builder canvas are capped at
 * 72rem, so a HALF group tops out around 30rem — any threshold at @md (28rem)
 * or above flattens FULL/HALF/THIRD into identical columns at real sizes.
 */
export const CQ_FIELD_GRID =
  "grid grid-cols-1 @xs:grid-cols-6 gap-3 w-full";

export const CQ_WIDTH_CLASS: Record<GuestDetailFieldWidth, string> = {
  FULL: "@xs:col-span-6",
  TWO_THIRDS: "@xs:col-span-4",
  HALF: "@xs:col-span-3",
  THIRD: "@xs:col-span-2",
};
/**
 * Section/group heading of every detail popup AND its builder canvas — one
 * family, one class. 16px bold, sentence case, no divider rule: the rhythm
 * comes from the section gap, not from a hairline.
 *
 * The three newer popups each grew their own `body-3-bold font-secondary
 * uppercase tracking-wide` copy (nine of them, one without the divider the
 * others had) while the guest popup and two canvases used this — so the same
 * section read as two different things depending on which surface you opened.
 *
 * ⚠ NOT the rail's PANEL heading — see {@link PANEL_HEADING_CLASS}.
 *
 * A const, not a component: two canvases render the heading as an editable
 * `<input>` (a component would only cover the `<p>` half and the other half
 * would drift straight back).
 */
export const SECTION_HEADING_CLASS = "body-1-bold";

/**
 * The rail PANEL's heading — a different job, deliberately a smaller scale.
 * It names a ~280px panel that an icon column cannot name, so it is chrome
 * rather than content structure; 16px bold there shouts over the fields.
 */
export const PANEL_HEADING_CLASS =
  "body-3-bold font-secondary uppercase tracking-wide";

export const WIDTH_CYCLE: GuestDetailFieldWidth[] = [
  "FULL",
  "TWO_THIRDS",
  "HALF",
  "THIRD",
];

export function isPlaceableField(
  field: Field,
  variant: LayoutVariant = "detail",
): boolean {
  return field.additionalField || builtinSetOf(variant).has(field.name);
}

/** All popup-placeable fields, in tray order (editable built-ins, then the
 *  display-only ones and — detail popup — the portal-link cell, then custom
 *  fields). */
export function placeableFields(
  allFields: Field[],
  variant: LayoutVariant = "detail",
): Field[] {
  const pool = withLayoutOnlyFields(allFields, variant);
  const builtins = builtinOrderOf(variant)
    .map((name) => pool.find((f) => !f.additionalField && f.name === name))
    .filter((f): f is Field => !!f);
  const additional = pool.filter((f) => f.additionalField);
  return [...builtins, ...additional];
}

export function fieldByKey(
  allFields: Field[],
  key: string,
  variant: LayoutVariant = "detail",
): Field | undefined {
  const f = findFieldByKey(withLayoutOnlyFields(allFields, variant), key);
  return f && isPlaceableField(f, variant) ? f : undefined;
}

/**
 * The hardcoded pre-layout popup structure as a layout: Allgemein / Kontakt /
 * Zusatzfelder. Titles are passed in already-translated — a default layout is
 * never persisted, so the headings stay live-localized until the admin first
 * customizes (then they become editable literals like any custom group name).
 */
export function buildDefaultLayout(
  allFields: Field[],
  titles: { general: string; contact: string; additional: string },
  variant: LayoutVariant = "detail",
): GuestDetailLayout {
  const fields = placeableFields(allFields, variant);
  // Mirrors the hardcoded pre-layout popup (GeneralInfos/ContactInfos):
  // gender/title/language sat 3-up, country was a lone full-width row, and
  // email:emailOptIn was a 7:3 row — TWO_THIRDS:THIRD fills the grid row
  // with the opt-in flush right, like the original.
  const width = (f: Field): GuestDetailFieldWidth => {
    if (f.additionalField) return "HALF";
    if (["gender", "title", "language", "emailOptIn"].includes(f.name))
      return "THIRD";
    if (f.name === "country") return "FULL";
    // The contact chips need the row (plan arrival-automation Rev. 4 §1.8).
    if (f.name === ARRIVAL_NOTICE_LAYOUT_KEY) return "FULL";
    if (f.name === "email") return "TWO_THIRDS";
    return "HALF";
  };
  const entry = (f: Field) => ({ key: getFieldKey(f), width: width(f) });

  // The opt-in floor: a display-only field — and the portal-link cell — is
  // NEVER in a default layout, so a fresh event and every `onReset` start
  // without one. Without this filter all of OPT_IN_LAYOUT_KEYS would land in
  // `general` for every event that never customized its popup — and the
  // builder's draft seed would then materialize them into Mongo on the next
  // save of anything.
  const general = fields.filter(
    (f) =>
      !f.additionalField &&
      !CONTACT_BUILTINS.has(f.name) &&
      !isOptInLayoutField(f),
  );
  const contact = fields.filter(
    (f) => !f.additionalField && CONTACT_BUILTINS.has(f.name),
  );
  const additional = fields.filter((f) => f.additionalField);

  // General + contact side by side (the popup's classic two-column shape),
  // custom fields across the full row beneath. Fields inside a HALF group
  // read their widths against the group, so HALF fields sit 2-up per row.
  // The additional group honors the 64-per-group cap; overflow spills into
  // synthetic groups so the default layout is always saveable.
  const groups: GuestDetailLayoutGroup[] = (
    [
      { id: "general", title: titles.general, width: "HALF", fields: general.map(entry) },
      { id: "contact", title: titles.contact, width: "HALF", fields: contact.map(entry) },
      {
        id: "additional",
        title: titles.additional,
        width: "FULL",
        fields: additional.slice(0, MAX_FIELDS_PER_GROUP).map(entry),
      },
    ] satisfies GuestDetailLayoutGroup[]
  ).filter((g) => g.fields.length > 0);
  return {
    groups: appendCapped(
      groups,
      additional.slice(MAX_FIELDS_PER_GROUP).map(getFieldKey),
    ),
  };
}

/** Backend cap on fields per group — appends must never push a group past it
 *  or every subsequent save would 400. Mirrors EventService's validator. */
export const MAX_FIELDS_PER_GROUP = 64;

/**
 * Append keys to the LAST group without breaching the per-group cap; overflow
 * spills into synthetic "auto-N" groups (deduped against existing ids) that
 * become real groups on the next save.
 */
/** The width an auto-appended key lands with: HALF, except the
 *  "Ankunft melden an" cell, whose chips need the row — FULL, as in the
 *  default layout (audit hop3 tests-blast F5). */
function appendedWidth(key: string): GuestDetailFieldWidth {
  return key === ARRIVAL_NOTICE_LAYOUT_KEY ? "FULL" : "HALF";
}

function appendCapped(
  groups: GuestDetailLayoutGroup[],
  keys: string[],
): GuestDetailLayoutGroup[] {
  if (keys.length === 0 || groups.length === 0) return groups;
  const out = [...groups];
  const existingIds = new Set(out.map((g) => g.id));
  let queue = [...keys];
  let last = out[out.length - 1];
  const room = Math.max(0, MAX_FIELDS_PER_GROUP - last.fields.length);
  if (room > 0) {
    out[out.length - 1] = {
      ...last,
      fields: [
        ...last.fields,
        ...queue.slice(0, room).map((key) => ({ key, width: appendedWidth(key) })),
      ],
    };
    queue = queue.slice(room);
  }
  let n = 1;
  while (queue.length > 0) {
    let id = `auto-${n}`;
    while (existingIds.has(id)) id = `auto-${++n}`;
    existingIds.add(id);
    out.push({
      id,
      fields: queue
        .slice(0, MAX_FIELDS_PER_GROUP)
        .map((key) => ({ key, width: appendedWidth(key) })),
    });
    queue = queue.slice(MAX_FIELDS_PER_GROUP);
    n++;
  }
  return out;
}

/**
 * Persisted layout -> renderable layout against the CURRENT field set:
 *  - keys whose field no longer exists (deleted / allowlist-hidden) are pruned
 *  - fields in neither a group nor `hidden` (created after the layout was
 *    saved) are auto-appended to the last group, so new fields never silently
 *    vanish; `hidden` keys stay hidden (that's the deliberate "removed" state)
 *  - REQUIRED fields never stay hidden: a required key in `hidden` (via a
 *    stale layout, a group deletion or a direct API write) is treated as
 *    unplaced and re-appended — a hidden required field would make its save
 *    violations invisible in the popup
 *  - appends respect the backend's 64-fields-per-group cap (overflow spills
 *    into synthetic groups) so a resolved layout is always saveable
 *  - OPT-IN keys (OPT_IN_LAYOUT_KEYS: the display-only built-ins and the
 *    portal-link cell) are exempt from the auto-append and normalized out of
 *    `hidden`: they render only where a group explicitly places them, so
 *    adding one to the vocabulary can never change a layout an admin already
 *    saved
 * Absent layout / empty groups falls back to the default layout.
 */
export function resolveLayout(
  layout: GuestDetailLayout | undefined,
  allFields: Field[],
  defaultTitles: { general: string; contact: string; additional: string },
  variant: LayoutVariant = "detail",
): GuestDetailLayout {
  if (!layout || layout.groups.length === 0) {
    return buildDefaultLayout(allFields, defaultTitles, variant);
  }
  const placeable = placeableFields(allFields, variant);
  const available = new Set(placeable.map(getFieldKey));
  // No read-only carve-out here on purpose. FieldsProvider does overlay
  // `required` from Event.fieldRequired onto built-ins, but the BACKEND refuses
  // to store such a key for a display-only field in the first place
  // (`EventService.validateFieldRequired` accepts only
  // CONDITIONABLE_BUILTIN_FIELDS, which contains none of them), and no
  // display-only entry in FIELDS_GUEST carries an intrinsic `required`. So the
  // state is unreachable, and the two filters below make it unobservable even
  // if it were. A third guard would be code no test can turn red.
  //
  // ⚠ If that backend allowlist ever widens, the BUILDER is where it bites, not
  // this resolver: `hideField`'s `if (field?.required) return d` and the
  // canvas' `disabled={field.required}` both run before the read-only branch,
  // so a required display-only cell could not be taken off the popup.
  const requiredKeys = new Set(
    placeable.filter((f) => f.required).map(getFieldKey),
  );
  let groups = layout.groups.map((g) => ({
    ...g,
    fields: g.fields.filter((f) => available.has(f.key)),
  }));
  const hidden = (layout.hidden ?? []).filter(
    (k) =>
      available.has(k) &&
      !requiredKeys.has(k) &&
      // An opt-in key has exactly TWO states: placed, or not placed.
      // Normalizing it out of `hidden` keeps the builder's two shelves
      // disjoint and makes the backend's "never in a group AND in hidden"
      // rule (EventService GDL validator) structurally unreachable.
      !isOptInLayoutKey(k),
  );

  const placed = new Set(groups.flatMap((g) => g.fields.map((f) => f.key)));
  const hiddenSet = new Set(hidden);
  const unplaced = [...available].filter(
    // Opt-in keys: "not in a group" IS their resting state, so they must
    // never ride the auto-append that exists for brand-new EDITABLE fields.
    // Auto-appending them would silently rewrite every saved layout.
    (k) => !placed.has(k) && !hiddenSet.has(k) && !isOptInLayoutKey(k),
  );
  groups = appendCapped(groups, unplaced);
  return { groups, hidden };
}

/**
 * A request DECISION lane's layout without the "Ankunft melden an" cell
 * (audit hop3 tests-blast F4). Those lanes resolve against the detail
 * vocabulary, where the cell is a default key — but their popup never renders
 * it (a pending request is a decision, not a guest who arrives), so the lane
 * builder must neither show it nor save it into the lane document. Removed
 * from every group and from `hidden`; a group the cell alone filled goes with
 * it (an empty group the admin left stays). Nothing to strip ⇒ the same
 * object back.
 */
export function withoutArrivalNotice<L extends GuestDetailLayout>(layout: L): L {
  const has =
    (layout.hidden ?? []).includes(ARRIVAL_NOTICE_LAYOUT_KEY) ||
    layout.groups.some((g) => g.fields.some((f) => f.key === ARRIVAL_NOTICE_LAYOUT_KEY));
  if (!has) return layout;
  return {
    ...layout,
    groups: layout.groups.flatMap((g) => {
      const fields = g.fields.filter((f) => f.key !== ARRIVAL_NOTICE_LAYOUT_KEY);
      return fields.length === 0 && g.fields.length > 0 ? [] : [{ ...g, fields }];
    }),
    ...(layout.hidden ? { hidden: layout.hidden.filter((k) => k !== ARRIVAL_NOTICE_LAYOUT_KEY) } : {}),
  };
}

/** True when the event carries a persisted custom layout (vs. the default). */
export function hasCustomLayout(layout: GuestDetailLayout | undefined): boolean {
  return !!layout && layout.groups.length > 0;
}
